import React, { useEffect, useState } from "react";
import { API } from "aws-amplify";
import YouTube from '../../components/YouTube';
import { querystring } from "../../libs/querystring";
import { processPolls } from "../../libs/processPolls";
import { processComments } from "../../libs/processComments";
import {
  Row,
  Col,
  Panel,
  Button,
  FormGroup,
  FormControl,
  Image
} from "react-bootstrap";
import { 
  ChevronLeft, 
  MessageSquare,
  X
} from 'react-feather';
import { Helmet } from 'react-helmet';
import "./PollResults.css";
import Comment from "../../components/Comment";
import Loader from "../../components/Loader";
import LoaderButton from "../../components/LoaderButton";
import PollHeader from "../../components/PollHeader";
import Results from "../../components/Results";
import dummyResults from "../../assets/dummy_results.png"
import { Client } from "@conversationai/perspectiveapi-js-client"

export default function PollResults(props) {
  const [poll, setPoll] = useState(null);
  const [comments, setComments] = useState(null);
  const [commentsShown, setCommentsShown] = useState(null);
  const [replies, setReplies] = useState(null);
  const [respData, setRespData] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [isInteracting, setIsInteracting] = useState(false);
  const [comment, setComment] = useState("");
  const [isCommenting, setIsCommenting] = useState(false);
  const [isRaw, setIsRaw] = useState(false);
  const [reply, setReply] = useState("");
  
  const [missingDemo, setMissingDemo] = useState(null);
  const [demoRespData, setDemoRespData] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [UID, setUID] = useState(null);

  const client = new Client('AIzaSyAVqI1QjMmGL-wVeFo0MUFHXTFIBA2hJGc');

  const demo_ques = {
    gender: {
      question_text: "What is your gender?",
      options: [
        {label: "Male", value: "male"}, 
        {label: "Female", value: "female"}, 
        {label: "Other", value: "other"}
      ]
    },
    age: {
      question_text: "What is your age group?",
      options: [
        {label: "< 18", value: "<18"}, 
        {label: "18 - 34", value: "18-34"}, 
        {label: "35 - 49", value: "35-49"},
        {label: "50 - 64", value: "50-64"},
        {label: "65 +", value: "65+"},
      ]
    },
    party: {
      question_text: "How do you identify yourself politically?",
      options: [
        {label: "Democrat", value: "democrat"}, 
        {label: "Lean Democrat", value: "lean-democrat"}, 
        {label: "Republican", value: "republican"}, 
        {label: "Lean Republican", value: "lean-republican"}, 
        {label: "Independent", value: "independent"},
        {label: "Other", value: "other"}
      ]
    },
    race: {
      question_text: "What is your race?",
      options: [
        {label: "White (non-hispanic)", value: "white"}, 
        {label: "Asian or Pacific Islander", value: "asian"}, 
        {label: "Black or African American", value: "black"},
        {label: "Hispanic or Latino", value: "hispanic"},
        {label: "Native American or American Indian", value: "nativeamerican"},
        {label: "Other", value: "other"}
      ]
    }, 
    education: {
      question_text: "What is your level of education?",
      options: [
        {label: "Less than a Bachelor's Degree", value: "highschool"}, 
        {label: "Bachelor's Degree or Higher", value: "bachelors"}
      ]
    },
    likely_voter: {
      question_text: "Are you likely to vote in the next election?",
      options: [
        {label: "Yes", value: true}, 
        {label: "No", value: false}
      ]
    },
    zip: {
      question_text: "What is your ZIP Code?"
    },
    email: {
      question_text: "What is your email?"
    }
  };

  useEffect(() => {
    // We wrap everything in an async function so we can "await" calls
    (async function loadPollData() {
      try {
        // 1) Check if email poll
        const isEmail = querystring("email") === "true";
        setUID(querystring("uid"));

        if (props.isAuthenticated) {
          setMissingDemo(querystring("demo"));
        } else {
          setMissingDemo(null);
        }

        // 2) Fetch the poll itself
        let path = "polls/" + props.match.params.poll_id;
        const pollResp = await API.get("polls", path);

        // If poll has < 100 votes, default to "raw" results
        if (pollResp.votes < 100) {
          setIsRaw(true);
        }

        // Community logic
        props.setIsMember(false);
        [...props.communities, ...props.myCommunities].forEach(comm => {
          if (comm.keyword === pollResp.keyword) {
            // If the user is in this community, set membership
            if (props.user.communities && props.user.communities.includes(comm.comm_id)) {
              props.setIsMember(true);
            }
            props.setCommunity(comm);
          }
        });

        // 3) If the user is logged in, fetch their poll response
        let userResponse = null;
        props.user.UID = localStorage.getItem("userUID");
        if (props.isAuthenticated && props.user.UID) {
          const params = { queryStringParameters: { UID: props.user.UID } };
          path = "polls_responses/" + props.match.params.poll_id;
          userResponse = await API.get("polls", path, params);
        }

        // 4) Process the poll (this sets canVote = false if `props.pollResponses` includes it)
        const [processed] = await processPolls(
          [pollResp],
          props.pollResponses,
          isEmail
        );
        let finalPoll = processed;

        // If the server confirms the user already voted, override canVote
        if (userResponse) {
          finalPoll.canVote = false;
          setRespData(userResponse);
        } else {
          setRespData({});
        }

        // 5) If the poll is still open to vote and this is not an email poll, redirect
        if (props.isAuthenticated && finalPoll.canVote && !isEmail) {
          props.history.push("/answer/" + props.match.params.poll_id);
        }

        // 6) If this poll is part of a series, fetch the other polls in that series
        if (finalPoll.series_id) {
          path = "polls_series/" + finalPoll.series_id;
          const seriesResp = await API.get("polls", path);
          finalPoll.series_data = await Promise.all(
            seriesResp.polls.map(async p => {
              const pollData = await API.get("polls", "polls/" + p.poll_id);
              return { label: p.label, poll: pollData };
            })
          );
        }

        // If fewer than 100 votes, force "raw" again
        if (finalPoll.votes < 100) {
          setIsRaw(true);
        }

        // Now set the final poll in state
        setPoll(finalPoll);

        // 7) Attempt to get comment interactions if logged in
        let interactions = {};
        if (props.isAuthenticated && props.user.UID) {
          const params = { queryStringParameters: { UID: props.user.UID } };
          path = "comment_interactions/" + props.match.params.poll_id;
          let interactionsResp = await API.get("comments", path, params);
          interactions = interactionsResp ? interactionsResp.interactions : {};
        }

        // 8) Fetch comments for this poll and process them
        path = "comments/" + props.match.params.poll_id;
        const commentsResp = await API.get("comments", path);
        const allComments = await processComments(
          commentsResp,
          interactions,
          props.user.UID
        );

        // Separate top-level comments from replies
        let topComments = [];
        let groupedReplies = {};

        allComments.forEach(comment => {
          if (comment.parent_id && !comment.deleted) {
            // It's a reply
            groupedReplies[comment.parent_id] = groupedReplies[comment.parent_id] || [];
            groupedReplies[comment.parent_id].push(comment);
          } else {
            // It's a top-level comment
            if (!comment.deleted) {
              topComments.push(comment);
            }
          }
        });

        // Sort top-level comments
        topComments.sort((a, b) => b.score - a.score);

        // Show first 5 or all if fewer than 6
        if (topComments.length < 6) {
          setShowAll(true);
          setCommentsShown(topComments);
        } else {
          setCommentsShown(topComments.slice(0, 5));
        }

        // Sort replies
        Object.values(groupedReplies).forEach(
          arr => arr.sort((a, b) => b.score - a.score)
        );

        setComments(topComments);
        setReplies(groupedReplies);
      } catch (error) {
        console.error("Error loading poll data", error);
        // Optionally set some error state
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function createComment(comment, parentCommentIdx=null) {
    if (!props.isAuthenticated) {
      props.history.push("/login?redirect=/results/" + props.match.params.poll_id);
      return;
    }
    if (isCommenting) return;

    // i want this to be prettier but this is easiest
    try {
      const object = await client.getScores(comment, {attributes: ["TOXICITY"]});
      let toxicityScore = object.TOXICITY;
      toxicityScore = (toxicityScore*100).toFixed(2);
      if (toxicityScore >= 90) {
        alert(`${toxicityScore} likely to be toxic. Please edit.`);
        return;
      }
      else if (toxicityScore >= 80) {
        const confirm = window.confirm(`${toxicityScore} likely to be toxic. Do you really want to send this?`);
        if (!confirm) {
          return;
        }
      }
    }
    // whatever
    catch (e) {
    }

    let parentCommentId = null;
    if (parentCommentIdx === null) {
      if (!comment) return;
      if (comment.length > 1000) {
        alert("Please limit observations to 1000 characters.");
        return;
      }
      setComment("");
    } else {
      if (!reply) return;
      if (reply.length > 1000) {
        alert("Please limit replies to 1000 characters.");
        return;
      }
      setReply("");
      parentCommentId = comments[parentCommentIdx].comment_id;
    }

    const data = {body: {
      poll_id: props.match.params.poll_id,
      text: comment,
      parent_id: parentCommentId,
      author_name: props.user.fullname,
      UID: props.user.UID
    }};
    setIsCommenting(true);
    API.post('comments', 'create_comment', data).then(resp => {
      let cmnt_new = {
        comment_id: resp,
        parent_id: parentCommentId,
        ups: 0,
        downs: 0,
        text: comment,
        award: 0,
        author: {
          UID: props.user.UID, 
          fullname: props.user.fullname, 
          avatarURL: props.avatar
        },
        time: "Just now",
        upVoted: false,
        downVoted: false,
        awarded: true,
        canDelete: true
      }
      if (!parentCommentId) {
        setComments([cmnt_new, ...comments]);
        setCommentsShown([cmnt_new, ...commentsShown]);
      } else {
        let replies_new;
        if (replies[parentCommentId])
          replies_new = [cmnt_new, ...replies[parentCommentId]];
        else 
          replies_new = [cmnt_new];
        setReplies(Object.assign({}, replies, {[parentCommentId]: replies_new}));
        comments[parentCommentIdx].showReply = false;
        comments[parentCommentIdx].showReplies = true;
        setComments([...comments]);
        setCommentsShown([...commentsShown]);
      }
      setPoll(Object.assign({}, poll, {comments: poll.comments + 1}));
      setIsCommenting(false);
    });
  }

  function deleteComment(cmnts, idx, ridx=null) {
    if (!props.isAuthenticated) {
      props.history.push("/login?redirect=/results/" + props.match.params.poll_id);
      return;
    }
    if (isCommenting) return;

    let cmnt_id = cmnts[idx].comment_id;
    let numRm = 1;
    if (ridx === null) { // deleting top level comment
      if (cmnt_id in replies)
        numRm = replies[cmnt_id].length + 1;
    } else { // deleting reply
      cmnt_id = replies[cmnt_id][ridx].comment_id;
    }
    const data = {body: {
      poll_id: props.match.params.poll_id,
      comment_id: cmnt_id,
      num_rm: numRm,
      UID: props.user.UID
    }};
    setIsCommenting(true);
    API.post('comments', 'delete_comment', data).then(resp => {
      if (ridx === null) { // deleting top level comment
        cmnts.splice(idx, 1);
        setComments(cmnts);
        commentsShown.splice(idx, 1);
        setCommentsShown(commentsShown);
      } else { // deleting reply
        replies[cmnts[idx].comment_id].splice(ridx, 1);
        setReplies(Object.assign({}, replies));
      }
      setPoll(Object.assign({}, poll, {comments: poll.comments - numRm})); 
      setIsCommenting(false); 
    });
  }

  function interact(cmnts, type, idx, ridx=null) {
    if (isInteracting) return;
    if (!props.isAuthenticated) {
      props.history.push("/login?redirect=/results/" + props.match.params.poll_id);
      return;
    }
    let comment = cmnts[idx];
    if (ridx !== null)
      comment = replies[cmnts[idx].comment_id][ridx];
    if (type === 'up') {
      if (!comment.upVoted) {
        if (comment.downVoted) {
          comment.downs -= 1;
          comment.downVoted = false;
        }
        comment.ups += 1
        comment.upVoted = true
      } else {
        return;
      }
    } else if (type === 'down') {
      if (!comment.downVoted) {
        if (comment.upVoted) {
          comment.ups -= 1;
          comment.upVoted = false;
        }
        comment.downs += 1
        comment.downVoted = true
      } else {
        return;
      }
    } else {
      if (props.user.icoins < 1) {
        alert("Not enough icoin! Earn more icoin before giving awards.");
        return;
      }
      if (!comment.awarded) {
        comment.award += 1
        comment.awarded = true

        const icoin = props.user.icoins - 1;
        props.setUser(Object.assign({}, props.user, {icoins: icoin}));
      } else {
        return;
      }
    }
    if (ridx === null) {
      cmnts[idx] = comment
      setComments(cmnts);
    } else {
      let replies_new = replies[cmnts[idx].comment_id];
      replies_new[ridx] = comment;
      setReplies(Object.assign({}, replies, {[cmnts[idx].comment_id]: replies_new}));
    }
    
    setIsInteracting(true);
    const data = {body: {
      poll_id: props.match.params.poll_id,
      comment_id: comment.comment_id,
      action: type,
      UID: props.user.UID
    }};
    if (type === 'award')
      data.body.author_id = comment.author.UID;
    API.post('comments', 'interact_comment', data).then(resp => setIsInteracting(false));
  }

  function submitDemo() {
    // check if demo question answered
    if (missingDemo === 'zip') {
      const zipPat = /^\d{5}$/;
      const isValid = zipPat.test(demoRespData.zip);
      if (!isValid) {
        alert("Please enter a valid Zip code.");
        setDemoRespData({zip: ""});
        return;
      }
    }
    if (missingDemo === 'email') {
      const emailPat = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
      const isValid = emailPat.test(demoRespData.email);
      if (!isValid) {
        alert("Please enter a valid email.");
        setDemoRespData({email: ""});
        return;
      }
    }
    if (Object.keys(demoRespData).length === 0) {
      alert("Please select an option.");
      return;
    }

    if (UID) {
      const body = Object.assign({}, {UID: UID}, demoRespData);
      setIsSubmitting(true);
      API.post('users', 'update_user', {body: body}).then(() => {
        setMissingDemo(false);
        setIsSubmitting(false);
      });
    } else { // is email -> update poll record with collected email
      setIsSubmitting(true);
      const path = 'add_email/' + props.match.params.poll_id;
      API.post('polls', path, {body: demoRespData}).then(() => {
        setMissingDemo(false);
        setIsSubmitting(false);
        localStorage.setItem('email', demoRespData.email);
      });
    }
  }

  function renderComments() {
    if (!comments || !replies || !commentsShown) return;
    return (
      <div style={{padding: '10px'}}>
        <h4><MessageSquare style={{verticalAlign: "-2px"}} size={18} /> {poll.comments} Observations</h4>
        <FormGroup style={{marginBottom: "0px"}}>
          <FormControl 
            style={{resize: "vertical"}} 
            onChange={e => {setComment(e.target.value)}}
            componentClass="textarea" 
            placeholder="Observation..."
            value={comment}
          />
        </FormGroup>
        <h5 className="warning-text" style={{marginBottom: "0px", marginTop: "4px"}}>
          icitizen is strictly nonpartisan and everyone is welcome to join and vote on polls. As a reminder, please keep your observations civil and courteous to others. Don't attack people. Personal insults, hate speech, profanity, any advocating or wishing death/physical harm, and other violations of civil discourse can result in a permanent ban at the sole discretion of icitizen. If you see posts in violation of our standards of conduct, please report them to <a href="mailto:team@icitizen.com?subject=Report User Poll">team@icitizen.com</a>.
        </h5>
        <button
          className="submit-button"
          onClick={() => createComment(comment)}
          isLoading={isCommenting}
        >
          Submit
        </button>
        {
          commentsShown.map((comment, idx) => (
            <Comment 
              key={idx}
              idx={idx}
              cmnts={commentsShown}
              comments={comments}
              comment={comment}
              replies={replies}
              createComment={createComment}
              deleteComment={deleteComment}
              interact={interact}
              setComments={setComments}
              setReply={setReply}
              isCommenting={isCommenting}
              reply={reply}
            />
          ))
        }
        {
          !showAll && 
            <Button 
              onClick={()=>{
                setShowAll(true);
                setCommentsShown(comments);
              }} 
              className="show-btn"
            >
              Show all observations
            </Button>
        }
      </div>
    );
  }

  function renderFooter() {
    return (
      <span 
        style={{paddingTop: "10px", cursor: "pointer"}} 
        className="text-muted"
        onClick={() => props.history.goBack()} 
      >
        <ChevronLeft />
        <span style={{verticalAlign: "7px"}}>
          Back
        </span>
      </span>
    )
  }

  function removePoll() {
    setIsSubmitting(true);
    const data = {
      PID: props.match.params.poll_id,
      UID: props.user.UID
    }
    API.post('polls', 'rm_poll/', {body: data}).then(() => {
      setIsSubmitting(false);
      const newUserData = Object.assign({}, props.user, {icoins: props.user.icoins + 9}); // give back icoin
      props.setUser(newUserData);
      props.history.push('/home');
    });
  }

  const opts = {
    height: props.width === 'sm' ? '240px' : '360px',
    width: '100%'
  };

  const renderResults = poll && respData !== null ? (
    <Panel className="poll-answer-panel">
      <Panel.Heading className="poll-answer-panel-heading">
        <PollHeader poll={poll} showCount={missingDemo !== 'email'}/>
      </Panel.Heading>
      { poll.video && <YouTube videoId={poll.video} opts={opts} /> }
      { poll.img && (<Image src={poll.img} className="vid" style={{width: '100%'}} />) }
      <Panel.Body style={{padding: 0}}>
        {
          !missingDemo ? 
            <Results poll={poll} respData={respData} isRaw={isRaw} width={props.width}/>
          :
            <Panel style={{padding: "15px", marginBottom: "8px"}}>
              <div className="bg-image" style={{backgroundImage: `url(${dummyResults})`}}></div>
              <Panel className='bg-text'>
              <h4 className="question-text">{ demo_ques[missingDemo].question_text }</h4>
              {
                missingDemo !== 'zip' && missingDemo !== 'email' ? (
                  <FormGroup style={{marginBottom: "-7px"}}>
                    {
                      demo_ques[missingDemo].options.map((opt, oidx) => {
                        return (
                          <div 
                            key={"demo:"+oidx} 
                            className="radio"
                            onChange={()=>setDemoRespData({[missingDemo]: opt.value})}
                          >
                            <input type="radio" id={"demo:"+oidx} name={"demo"}/>
                            <label htmlFor={"demo:"+oidx}>{ opt.label }</label>
                          </div>
                        )
                      })
                    }
                  </FormGroup>
                ) : (
                  <FormGroup style={{marginBottom: 0}}>
                    <div style={{marginTop: "7px", width: "200px"}} className="input-group">
                      <FormControl
                        style={{borderRadius: "4px"}}
                        value={demoRespData[missingDemo] ? demoRespData[missingDemo] : ""}
                        onChange={e => setDemoRespData({[missingDemo]: e.target.value})}
                      />
                    </div>
                  </FormGroup>
                )
              }
              <div style={{display: "flex", marginTop: "-4px", "marginBottom": "-3px"}}>
                <LoaderButton
                  onClick={submitDemo}
                  isLoading={isSubmitting}
                  style={{marginLeft: 'auto'}}
                >
                  Submit
                </LoaderButton>
              </div>
              </Panel>
            </Panel>
        }
        { poll.author.UID === props.user.UID &&
          <Button 
            block
            className="vote-btn"
            disabled={isSubmitting}
            onClick={()=>{
              if (isSubmitting) return;
              removePoll();
            }}
          >
           <X style={{width: "17px", verticalAlign: "-7px"}} /> Remove Poll
          </Button>
        }
        { renderComments() }
      </Panel.Body>
      <div className="poll-answer-panel-heading" style={{padding: '0 0 10px 10px'}}>
        { renderFooter() }
      </div>
    </Panel>
  ) : null;

  return poll && respData !== null ? (
    <div>
      <Helmet>
        <title>{poll.title} | icitizen</title>
        <meta name="title" content={`${poll.title} | icitizen`}/>
        <meta name="description" content={poll.questions[0].question_text}/>
        <meta property="og:type" content="website"/>
        <meta property="og:url" content={window.location.href}/>
        <meta property="og:title" content={`${poll.title}`}/>
        <meta property="og:description" content={poll.questions[0].question_text}/>
        <meta property="og:image" content={poll.img ? poll.img : "https://icitizen.com/ic_card.png"}/>
        <meta property="twitter:card" content="summary_large_image"/>
        <meta property="twitter:url" content={window.location.href}/>
        <meta property="twitter:title" content={`${poll.title} | icitizen`}/>
        <meta property="twitter:description" content={poll.questions[0].question_text}/>
        <meta property="twitter:image" content={poll.img ? poll.img : "https://icitizen.com/ic_card.png"}/> 
      </Helmet>
      <Row className="content-spaced">
        <Col xs={12}>
          { renderResults }
        </Col>
      </Row>
    </div>
  ) : (<Loader />);
}
